import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  LoyaltyPeriodDto,
  useCreateLoyaltyPeriod,
  useDeleteLoyaltyPeriod,
  useLoyaltyPeriods,
  useUpdateLoyaltyPeriod,
} from '@shared/api/hooks/useLoyaltyPeriods';
import { useTenantAdminConfig, useUpdateTenantConfig } from '@shared/api/hooks/useTenantAdminConfig';

interface PeriodFormValues {
  name: string;
  starts: Dayjs;
  ends: Dayjs;
}

/** Overlap fra [aS,aE) e [bS,bE): aS < bE AND bS < aE. Finestre semiaperte:
 *  due periodi che si toccano al confine NON si sovrappongono. */
export function overlaps(aS: Dayjs, aE: Dayjs, bS: Dayjs, bE: Dayjs): boolean {
  return aS.isBefore(bE) && bS.isBefore(aE);
}

/**
 * Anno di inizio più vecchio fra gli ALTRI periodi (esclude quello in modifica).
 * null se non ce ne sono.
 */
export function earliestOtherStartYear(
  periods: LoyaltyPeriodDto[],
  excludeId: string | null,
): number | null {
  return periods
    .filter((p) => p.id !== excludeId)
    .reduce<number | null>((min, p) => {
      const y = dayjs(p.startsAt).year();
      return min === null || y < min ? y : min;
    }, null);
}

/** true se un inizio nell'anno `startYear` abbassa il minimo → sposta la stagione. */
export function wouldShiftSeason(
  startYear: number | undefined,
  earliestYear: number | null,
): boolean {
  return startYear != null && earliestYear !== null && startYear < earliestYear;
}

export function LoyaltyPeriodsPage(): React.ReactElement {
  const { data: periods = [], isLoading, refetch, isFetching } = useLoyaltyPeriods();
  const createMutation = useCreateLoyaltyPeriod();
  const updateMutation = useUpdateLoyaltyPeriod();
  const deleteMutation = useDeleteLoyaltyPeriod();

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form] = Form.useForm<PeriodFormValues>();
  const watchedStart = Form.useWatch('starts', form);

  // T-116 (decisione god): niente divieto di retrodatazione, ma un AVVISO. La
  // "stagione" (label FanTier/limiti sconti) = anno del primo inizio fra tutti i
  // periodi. Un periodo che inizia in un ANNO precedente al più vecchio esistente
  // abbassa quel minimo e SPOSTA la stagione. Avvisiamo prima di salvare, con
  // l'effetto scritto in chiaro; non blocchiamo (aggiungere un periodo dimenticato
  // è legittimo).
  const shiftsSeason = wouldShiftSeason(
    watchedStart?.year(),
    earliestOtherStartYear(periods, editId),
  );

  const openCreate = (): void => {
    setEditId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (p: LoyaltyPeriodDto): void => {
    setEditId(p.id);
    form.setFieldsValue({ name: p.name, starts: dayjs(p.startsAt), ends: dayjs(p.endsAt) });
    setModalOpen(true);
  };

  const onSubmit = async (): Promise<void> => {
    const v = await form.validateFields();
    if (!v.starts.isBefore(v.ends)) {
      message.error('La data di inizio deve precedere la data di fine.');
      return;
    }
    // Controllo client immediato; il backend resta la fonte di verità (409).
    const clash = periods.find(
      (p) =>
        p.id !== editId &&
        overlaps(v.starts, v.ends, dayjs(p.startsAt), dayjs(p.endsAt)),
    );
    if (clash) {
      message.error(`Si sovrappone al periodo "${clash.name}". I periodi non possono sovrapporsi.`);
      return;
    }
    const input = {
      name: v.name.trim(),
      startsAt: v.starts.toISOString(),
      endsAt: v.ends.toISOString(),
    };
    try {
      if (editId) {
        await updateMutation.mutateAsync({ id: editId, input });
        message.success('Periodo aggiornato');
      } else {
        await createMutation.mutateAsync(input);
        message.success('Periodo creato');
      }
      setModalOpen(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore di salvataggio'));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Periodo eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore eliminazione');
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <RegulationCard />

      <Card
        title={
          <Space>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Periodi di raccolta
            </Typography.Title>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Aggiorna
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Nuovo periodo
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Le fasi di raccolta punti sono periodi veri, creabili senza limite."
          description="Ogni periodo è una finestra [inizio, fine). I periodi non possono sovrapporsi (validato anche lato server); i buchi tra periodi sono ammessi — i punti guadagnati fuori da ogni periodo restano nel totale a vita, ma non entrano in nessuna classifica di fase."
        />
        <Table<LoyaltyPeriodDto>
          rowKey="id"
          loading={isLoading}
          dataSource={periods}
          pagination={false}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Nome', dataIndex: 'name' },
            {
              title: 'Inizio',
              dataIndex: 'startsAt',
              render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm'),
            },
            {
              title: 'Fine',
              dataIndex: 'endsAt',
              render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm'),
            },
            {
              title: '',
              width: 100,
              align: 'right',
              render: (_v, r) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                  <Popconfirm
                    title="Eliminare il periodo?"
                    okText="Elimina"
                    cancelText="Annulla"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => onDelete(r.id)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editId ? 'Modifica periodo' : 'Nuovo periodo'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        okText={editId ? 'Salva' : 'Crea'}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          {shiftsSeason && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Questo periodo inizia in un anno precedente al più vecchio esistente"
              description={
                `Sposterà la “stagione” all’anno ${watchedStart?.year()}: cambia i limiti degli sconti ` +
                `“per stagione” e il reset dei punti-stagione. È consentito (es. aggiungere un periodo ` +
                `dimenticato), ma verifica che sia ciò che vuoi prima di salvare.`
              }
            />
          )}
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, max: 80 }]}
          >
            <Input placeholder="Es. Fase 1" />
          </Form.Item>
          <Form.Item name="starts" label="Inizio" rules={[{ required: true }]}>
            <DatePicker
              style={{ width: '100%' }}
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
            />
          </Form.Item>
          <Form.Item name="ends" label="Fine" rules={[{ required: true }]}>
            <DatePicker
              style={{ width: '100%' }}
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}

/** Card per l'URL del PDF del regolamento loyalty (spostato dalle Impostazioni). */
function RegulationCard(): React.ReactElement {
  const { data: config } = useTenantAdminConfig();
  const updateMutation = useUpdateTenantConfig();
  const [form] = Form.useForm<{ loyaltyRegulationUrl?: string }>();

  React.useEffect(() => {
    form.setFieldsValue({ loyaltyRegulationUrl: config?.loyaltyRegulationUrl ?? undefined });
  }, [config?.loyaltyRegulationUrl, form]);

  const onSave = async (): Promise<void> => {
    const v = await form.validateFields();
    try {
      await updateMutation.mutateAsync({ loyaltyRegulationUrl: v.loyaltyRegulationUrl || null });
      message.success('Regolamento salvato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore di salvataggio'));
    }
  };

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Regolamento Loyalty
        </Typography.Title>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="loyaltyRegulationUrl"
          label="URL regolamento loyalty (PDF)"
          tooltip="PDF del regolamento raccolta punti — mostrato in app. Carica il file nel Repository Media e incolla qui l'URL."
          rules={[{ type: 'url', message: 'Inserisci un URL valido' }]}
        >
          <Input placeholder="https://..." allowClear />
        </Form.Item>
        <Button type="primary" onClick={onSave} loading={updateMutation.isPending}>
          Salva regolamento
        </Button>
      </Form>
    </Card>
  );
}
